import {
  ANALYTICS_EVENT_AREAS,
  ANALYTICS_EVENT_NAMES,
  type AnalyticsEventArea,
  type AnalyticsEventName,
} from "@lafayette/schemas";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

// What the Events tab keeps in the address (`?tab=events&area=app&event=app_opened&only=<visit id>&visit=<visit id>&open=<event uuid>&from=..&to=..&q=text`), so a link, a reload and
// the back button hold the view. A value that is not on a closed list is dropped, never an error. The ids are checked for their shape only: the
// server decides whether they are this customer's.

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;

/** The keys that the Events tab changes. `visit` opens the visit sheet (the address that Explore's "Open visit" builds); `only` filters the table. */
type EventsKey = "area" | "event" | "visit" | "only" | "open" | "q" | "from" | "to";

export type EventsAddress = Readonly<{
  area: AnalyticsEventArea | null;
  event: AnalyticsEventName | null;
  /** The visit whose events are the only ones in the table (`only`). */
  onlyVisitId: string | null;
  /** The visit whose sheet is open (`visit`): one visit step by step. */
  visitId: string | null;
  openId: string | null;
  /** The time window of the table inside the range (`from` and `to`, both or neither: the window that Explore zoomed to). */
  window: Readonly<{ from: string; to: string }> | null;
  search: string;
  /** Changes some keys (a `null` removes one). Other keys, the customer, the tab and the range stay. */
  set: (changes: Partial<Record<EventsKey, string | null>>) => void;
}>;

const inList = <T extends string>(list: readonly T[], value: string | null): T | null =>
  value !== null && (list as readonly string[]).includes(value) ? (value as T) : null;
const uuidOf = (value: string | null): string | null =>
  value !== null && UUID.test(value) ? value : null;

/** A window from the address: both ends are instants, and the end is after the start. Otherwise there is none. */
function windowOf(from: string | null, to: string | null): EventsAddress["window"] {
  if (from === null || to === null) return null;
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null;
  return { from: new Date(start).toISOString(), to: new Date(end).toISOString() };
}

export function useEventsAddress(): EventsAddress {
  const [search, setSearch] = useSearchParams();
  const set = useCallback<EventsAddress["set"]>(
    (changes) =>
      setSearch(
        (current) => {
          const copy = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === "") copy.delete(key);
            else copy.set(key, value);
          }
          return copy;
        },
        { replace: true },
      ),
    [setSearch],
  );
  const area = inList(ANALYTICS_EVENT_AREAS, search.get("area"));
  const event = inList(ANALYTICS_EVENT_NAMES, search.get("event"));
  const onlyVisitId = uuidOf(search.get("only"));
  const visitId = uuidOf(search.get("visit"));
  const openId = uuidOf(search.get("open"));
  const from = search.get("from");
  const to = search.get("to");
  const text = (search.get("q") ?? "").slice(0, 64);
  return useMemo(
    () => ({
      area,
      event,
      onlyVisitId,
      visitId,
      openId,
      window: windowOf(from, to),
      search: text,
      set,
    }),
    [area, event, onlyVisitId, visitId, openId, from, to, text, set],
  );
}
